import '../participation.css';
import {
  calcBbox,
  makeProjector,
  polygonToSvgD,
  lineToSvgD,
  filterEdgesNearCenter,
} from '../utils/geo-to-svg';

// ── Config ────────────────────────────────────────────────────────────────────

// Real centroid of SCLC (from PostGIS ST_Centroid)
const CENTRO_LNG = -92.6376;
const CENTRO_LAT = 16.7370;

// Radius for the street-thumbnail (degrees ≈ 800m)
const THUMB_RADIUS = 0.007;

// ── SVG illustrations ─────────────────────────────────────────────────────────

interface CityShapeProps {
  boundary: GeoJSON.Feature | null;
}

function CityShapeSVG({ boundary }: CityShapeProps): JSX.Element {
  const VW = 200;
  const VH = 155;

  if (!boundary) {
    // Skeleton while loading
    return (
      <svg viewBox={`0 0 ${VW} ${VH}`} aria-hidden>
        <rect x="20" y="15" width="160" height="125" rx="4" fill="#f1f5f9" />
      </svg>
    );
  }

  const geom = boundary.geometry as GeoJSON.MultiPolygon;
  const bbox = calcBbox(geom);
  const project = makeProjector(bbox, VW, VH, 10);
  const pathD = polygonToSvgD(geom, project);

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        className="city-shape"
        d={pathD}
        fill="rgba(26,26,46,0.04)"
        stroke="#9ca3af"
        strokeWidth="1.3"
        strokeLinejoin="round"
        style={{ transition: 'fill 0.22s, stroke 0.22s, stroke-width 0.22s' }}
      />
    </svg>
  );
}

// ── Sector shape ──────────────────────────────────────────────────────────────

interface SectorShapeProps {
  boundary: GeoJSON.Feature | null;
}

function SectorShapeSVG({ boundary }: SectorShapeProps): JSX.Element {
  const VW = 200;
  const VH = 155;

  if (!boundary) {
    return (
      <svg viewBox={`0 0 ${VW} ${VH}`} aria-hidden>
        <rect x="20" y="15" width="160" height="125" rx="4" fill="#f1f5f9" />
      </svg>
    );
  }

  const geom = boundary.geometry as GeoJSON.MultiPolygon;
  const bbox = calcBbox(geom);
  const project = makeProjector(bbox, VW, VH, 10);
  const pathD = polygonToSvgD(geom, project);

  // Divide at the real centroid latitude (Norte / Sur split)
  // NOTE: provisional sectorization — splits at centroid latitude.
  // Replace when real urban sector boundaries are loaded into spatial_grids
  // with a meaningful aggregation (e.g., grouping H3 cells by zone name).
  const centroidLat = 16.6784;
  const [, splitY] = project(CENTRO_LNG, centroidLat);

  // Clip-path IDs must be unique in the document
  const idN = 'sclc-clip-north';
  const idS = 'sclc-clip-south';

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <clipPath id={idN}>
          <rect x="0" y="0" width={VW} height={splitY} />
        </clipPath>
        <clipPath id={idS}>
          <rect x="0" y={splitY} width={VW} height={VH - splitY} />
        </clipPath>
      </defs>

      {/* North zone fill */}
      <path
        className="zone-north"
        d={pathD}
        fill="rgba(59,130,246,0.10)"
        clipPath={`url(#${idN})`}
        style={{ transition: 'fill 0.22s' }}
      />
      {/* South zone fill */}
      <path
        className="zone-south"
        d={pathD}
        fill="rgba(249,115,22,0.08)"
        clipPath={`url(#${idS})`}
        style={{ transition: 'fill 0.22s' }}
      />
      {/* Boundary outline */}
      <path
        className="sector-boundary"
        d={pathD}
        fill="none"
        stroke="#9ca3af"
        strokeWidth="1.3"
        strokeLinejoin="round"
        style={{ transition: 'stroke 0.22s, stroke-width 0.22s' }}
      />
      {/* Horizontal divider */}
      <line
        className="sector-divider"
        x1="8"
        y1={splitY}
        x2={VW - 8}
        y2={splitY}
        stroke="#cbd5e1"
        strokeWidth="1.2"
        strokeDasharray="5 3"
        style={{ transition: 'stroke 0.22s' }}
      />
      {/* Zone labels */}
      <text x={VW / 2} y={splitY - 12} textAnchor="middle" fontSize="9" fill="#94a3b8" fontWeight="500">Norte</text>
      <text x={VW / 2} y={splitY + 18} textAnchor="middle" fontSize="9" fill="#94a3b8" fontWeight="500">Sur</text>
    </svg>
  );
}

// ── Place shape (real street network thumbnail) ───────────────────────────────

interface PlaceShapeProps {
  network: GeoJSON.FeatureCollection | null;
}

function PlaceShapeSVG({ network }: PlaceShapeProps): JSX.Element {
  const VW = 180;
  const VH = 155;
  const cx = VW / 2;
  const cy = VH / 2;
  const r = 62;
  const clipId = 'sclc-place-clip';

  if (!network) {
    return (
      <svg viewBox={`0 0 ${VW} ${VH}`} aria-hidden>
        <circle cx={cx} cy={cy} r={r} fill="#f1f5f9" />
      </svg>
    );
  }

  // Keep only edges near the historical center of SCLC
  const near = filterEdgesNearCenter(network.features, CENTRO_LNG, CENTRO_LAT, THUMB_RADIUS);

  // Build bbox around the center window (square)
  const thumbBbox = {
    minLng: CENTRO_LNG - THUMB_RADIUS,
    maxLng: CENTRO_LNG + THUMB_RADIUS,
    minLat: CENTRO_LAT - THUMB_RADIUS,
    maxLat: CENTRO_LAT + THUMB_RADIUS,
  };
  const project = makeProjector(thumbBbox, VW, VH, (VW - 2 * r) / 2 + 2);

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <clipPath id={clipId}>
          <circle cx={cx} cy={cy} r={r - 1} />
        </clipPath>
      </defs>

      {/* Background circle */}
      <circle cx={cx} cy={cy} r={r} fill="rgba(26,26,46,0.03)" />

      {/* Real street network, clipped to circle */}
      <g clipPath={`url(#${clipId})`}>
        {near.map((f, i) => {
          const g = f.geometry as GeoJSON.LineString | GeoJSON.MultiLineString;
          if (g.type !== 'LineString' && g.type !== 'MultiLineString') return null;
          return (
            <path
              key={i}
              className="street-line"
              d={lineToSvgD(g, project)}
              stroke="#d1d5db"
              strokeWidth="1"
              strokeLinecap="round"
              style={{ transition: 'stroke 0.22s, stroke-width 0.22s' }}
            />
          );
        })}
      </g>

      {/* Circle border */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        stroke="#9ca3af"
        strokeWidth="1.4"
        className="place-circle"
        style={{ transition: 'stroke 0.22s' }}
      />

      {/* Location pin */}
      <g transform={`translate(${cx}, ${cy - 6})`}>
        <path
          className="place-pin-body"
          d="M0,-13 C-6,-13 -6,-6 -6,0 C-6,5 0,13 0,13 C0,13 6,5 6,0 C6,-6 6,-13 0,-13 Z"
          fill="#9ca3af"
          style={{ transition: 'fill 0.22s' }}
        />
        <circle className="place-pin-dot" cx="0" cy="-2" r="3" fill="#fff" />
      </g>
    </svg>
  );
}

// ── Card types ────────────────────────────────────────────────────────────────

export type CardScale = 'city' | 'sector' | 'place';

interface HeroCardProps {
  title: string;
  description: string;
  visual: JSX.Element;
  onClick: () => void;
  isActive: boolean;
  a11yMode?: boolean;
  /** Optional label shown as a small action pill at the bottom of the card */
  cta?: string;
}

function HeroCard({ title, description, visual, onClick, isActive, a11yMode, cta }: HeroCardProps): JSX.Element {
  const cardBg   = a11yMode ? '#1e293b' : '#fff';
  const cardBdr  = a11yMode ? '#334155' : '#e8e8e8';
  const titleClr = a11yMode ? '#f1f5f9' : '#1a1a2e';
  const descClr  = a11yMode ? '#94a3b8' : '#6b7280';
  const arrowClr = a11yMode ? '#60a5fa' : '#9ca3af';
  const ctaBg    = a11yMode ? '#1e3a5f' : '#f1f5f9';
  const ctaClr   = a11yMode ? '#93c5fd' : '#374151';
  const fsTitle  = a11yMode ? 20 : 15;
  const fsDesc   = a11yMode ? 16 : 12.5;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`participation-hero-card${isActive ? ' card-active' : ''}`}
      style={{
        flex: '1 1 220px',
        minWidth: 0,
        background: cardBg,
        border: `1.5px solid ${cardBdr}`,
        borderRadius: 18,
        padding: '22px 20px 20px',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        cursor: 'pointer',
        boxShadow: a11yMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 6px rgba(0,0,0,0.04)',
        transition: 'background 0.2s, border-color 0.2s',
      }}
    >
      <div style={{ width: '100%', aspectRatio: '6/4', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        <div style={{ width: '85%', maxWidth: 170 }}>
          {visual}
        </div>
      </div>
      <div style={{ marginTop: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: fsTitle, fontWeight: 700, color: titleClr, lineHeight: 1.2 }}>{title}</span>
          <span className="card-arrow" style={{ color: arrowClr, fontSize: 15, lineHeight: 1 }}>→</span>
        </div>
        <p style={{ margin: 0, fontSize: fsDesc, color: descClr, lineHeight: 1.5 }}>{description}</p>
        {cta && (
          <span style={{
            display: 'inline-block',
            marginTop: 10,
            padding: '4px 10px',
            borderRadius: 20,
            background: ctaBg,
            color: ctaClr,
            fontSize: a11yMode ? 15 : 11.5,
            fontWeight: 600,
            letterSpacing: '0.01em',
          }}>
            {cta}
          </span>
        )}
      </div>
    </button>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

interface HeroCardsProps {
  boundary: GeoJSON.Feature | null;
  network: GeoJSON.FeatureCollection | null;
  activeCard: CardScale | null;
  a11yMode?: boolean;
  onCityClick: () => void;
  onSectorClick: () => void;
  onPlaceClick: () => void;
}

export function HeroCards({
  boundary,
  network,
  activeCard,
  a11yMode,
  onCityClick,
  onSectorClick,
  onPlaceClick,
}: HeroCardsProps): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      <HeroCard
        title="Ciudad completa"
        description="Comparte una perspectiva sobre el municipio en general."
        visual={<CityShapeSVG boundary={boundary} />}
        onClick={onCityClick}
        isActive={activeCard === 'city'}
        a11yMode={a11yMode}
      />
      <HeroCard
        title="Sector urbano"
        description="Aporta sobre una zona de referencia de la ciudad."
        visual={<SectorShapeSVG boundary={boundary} />}
        onClick={onSectorClick}
        isActive={activeCard === 'sector'}
        a11yMode={a11yMode}
      />
      <HeroCard
        title="Calle o lugar"
        description="Selecciona una calle o punto directamente en el mapa."
        visual={<PlaceShapeSVG network={network} />}
        onClick={onPlaceClick}
        isActive={activeCard === 'place'}
        a11yMode={a11yMode}
        cta="Ir al mapa →"
      />
    </div>
  );
}
